// ─────────────────────────────────────────────────────────────────────────────
// margin_graphics.ts — Themed ornaments occupying the margin band
//
// Where utility marks anchor snug to the body (gutter), graphics inhabit
// the full margin space — centred or sprawled across one or more of the
// four margin bands (left / right / top / bottom). Each graphic decides
// which sides it occupies and how it shapes itself within them.
//
// Examples: greek columns (left + right), floral vines (left + right),
// arabic arches (top + bottom, matching the mosaic body-graphic theme).
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';
import { marginDrawBox } from './margin_utility';

export interface MarginGraphic {
  id: string;
  label: string;
  draw(ctx: CanvasRenderingContext2D, layout: PageLayout): void;
}

/** Vertical margin bands — left / right rectangles between page edge
 *  and body content. Top/bottom clipped by the marginDrawBox so the
 *  page header isn't clobbered. */
function verticalBands(L: PageLayout): {
  left:  { x: number; y: number; w: number; h: number; cx: number };
  right: { x: number; y: number; w: number; h: number; cx: number };
} {
  const box = marginDrawBox(L);
  const top = Math.max(box.top, L.y + L.marginTop * 0.3);
  const bot = box.bottom;
  const leftX  = L.x + 4;
  const leftW  = L.marginLeft - 8;
  const rightX = L.x + L.width - L.marginRight + 4;
  const rightW = L.marginRight - 8;
  return {
    left:  { x: leftX,  y: top, w: leftW,  h: bot - top, cx: leftX  + leftW  / 2 },
    right: { x: rightX, y: top, w: rightW, h: bot - top, cx: rightX + rightW / 2 },
  };
}

/** Horizontal margin bands — top / bottom rectangles between the
 *  header strip and the body, and between the body and the page footer. */
function horizontalBands(L: PageLayout): {
  top:    { x: number; y: number; w: number; h: number; cy: number };
  bottom: { x: number; y: number; w: number; h: number; cy: number };
} {
  const box = marginDrawBox(L);
  const innerX = L.x + L.marginLeft;
  const innerW = L.width - L.marginLeft - L.marginRight;
  const topTop = box.top;
  const topBot = L.y + L.marginTop;
  const botTop = L.y + L.height - L.marginBottom;
  const botBot = box.bottom;
  return {
    top:    { x: innerX, y: topTop, w: innerW, h: Math.max(0, topBot - topTop),
              cy: (topTop + topBot) / 2 },
    bottom: { x: innerX, y: botTop, w: innerW, h: Math.max(0, botBot - botTop),
              cy: (botTop + botBot) / 2 },
  };
}

// ── Built-ins ───────────────────────────────────────────────────────────────

const NONE: MarginGraphic = {
  id: 'none',
  label: 'None',
  draw: () => { /* nothing */ },
};

/** Greek columns — pair of fluted vertical columns centred in left and
 *  right margins, with simple capital + base blocks at top and bottom. */
const GREEK_COLUMNS: MarginGraphic = {
  id: 'greek-columns',
  label: 'Greek columns',
  draw(ctx, L) {
    const { left, right } = verticalBands(L);
    const drawColumn = (cx: number, top: number, bot: number) => {
      const shaftW = 14;
      const capW = 22;
      const blockH = 9;
      // Capital
      ctx.fillStyle = 'rgba(110, 80, 50, 0.32)';
      ctx.fillRect(cx - capW / 2, top, capW, blockH);
      ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
      ctx.lineWidth = 0.7;
      ctx.strokeRect(cx - capW / 2, top, capW, blockH);
      // Echinus + abacus highlight
      ctx.beginPath();
      ctx.moveTo(cx - capW / 2, top + blockH);
      ctx.lineTo(cx + capW / 2, top + blockH);
      ctx.stroke();
      // Shaft
      const shaftTop = top + blockH;
      const shaftBot = bot - blockH;
      ctx.fillStyle = 'rgba(110, 80, 50, 0.14)';
      ctx.fillRect(cx - shaftW / 2, shaftTop, shaftW, shaftBot - shaftTop);
      // Flutes — three vertical hairlines
      ctx.strokeStyle = 'rgba(110, 80, 50, 0.40)';
      ctx.lineWidth = 0.55;
      ctx.beginPath();
      for (const dx of [-shaftW / 3, 0, shaftW / 3]) {
        ctx.moveTo(cx + dx, shaftTop);
        ctx.lineTo(cx + dx, shaftBot);
      }
      // Shaft outline
      ctx.moveTo(cx - shaftW / 2, shaftTop); ctx.lineTo(cx - shaftW / 2, shaftBot);
      ctx.moveTo(cx + shaftW / 2, shaftTop); ctx.lineTo(cx + shaftW / 2, shaftBot);
      ctx.stroke();
      // Base
      ctx.fillStyle = 'rgba(110, 80, 50, 0.32)';
      ctx.fillRect(cx - capW / 2, bot - blockH, capW, blockH);
      ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
      ctx.strokeRect(cx - capW / 2, bot - blockH, capW, blockH);
    };
    ctx.save();
    drawColumn(left.cx,  left.y,  left.y  + left.h);
    drawColumn(right.cx, right.y, right.y + right.h);
    ctx.restore();
  },
};

/** Floral vines — climbing botanicals centred in each vertical margin.
 *  Thicker / leafier than the prior gutter version: lives in the
 *  margin band rather than hugging the body. */
const FLORAL_VINES: MarginGraphic = {
  id: 'floral-vines',
  label: 'Floral vines',
  draw(ctx, L) {
    const { left, right } = verticalBands(L);
    const drawVine = (band: { cx: number; y: number; h: number }, mirror: boolean) => {
      const sign = mirror ? -1 : 1;
      const ampl = Math.min(14, (mirror ? right.w : left.w) * 0.32);
      const wave = 44;
      ctx.beginPath();
      for (let t = 0; t <= band.h; t += 4) {
        const phase = (t / wave) * Math.PI * 2;
        const x = band.cx + sign * Math.sin(phase) * ampl;
        if (t === 0) ctx.moveTo(x, band.y + t);
        else ctx.lineTo(x, band.y + t);
      }
      ctx.stroke();
      // Leaves at each half-wave
      const leaves = Math.floor(band.h / (wave / 2));
      for (let i = 0; i < leaves; i++) {
        const t = (i + 0.5) * (wave / 2);
        if (t > band.h) break;
        const phase = (t / wave) * Math.PI * 2;
        const x = band.cx + sign * Math.sin(phase) * ampl;
        const y = band.y + t;
        const cosp = Math.cos(phase);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(sign * (cosp * 0.9));
        ctx.beginPath();
        ctx.ellipse(sign * 7, 0, 7, 3.2, 0, 0, Math.PI * 2);
        ctx.fill();
        // Tiny bud
        ctx.beginPath();
        ctx.arc(sign * 12, 0, 1.4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    };
    ctx.save();
    ctx.strokeStyle = 'rgba(85, 105, 60, 0.65)';
    ctx.fillStyle   = 'rgba(85, 105, 60, 0.45)';
    ctx.lineWidth   = 0.85;
    drawVine(left,  false);
    drawVine(right, true);
    ctx.restore();
  },
};

/** Arabic arches — a row of pointed (lancet) arches across the top and
 *  bottom margins. Pairs visually with the mosaic body graphic. */
const ARABIC_ARCHES: MarginGraphic = {
  id: 'arabic-arches',
  label: 'Arabic arches',
  draw(ctx, L) {
    const { top, bottom } = horizontalBands(L);
    if (top.w <= 0 || bottom.w <= 0) return;
    const count = Math.max(4, Math.floor(top.w / 64));
    const cellW = top.w / count;
    const arch = (cx: number, baseline: number, height: number, flipped: boolean) => {
      const halfW = cellW * 0.42;
      const dir = flipped ? -1 : 1;
      const tip = baseline - dir * height;
      const peakHeight = height * 1.05;
      ctx.beginPath();
      ctx.moveTo(cx - halfW, baseline);
      // Pointed (lancet) arch: two quadratic curves meeting at the tip
      ctx.quadraticCurveTo(cx - halfW * 0.55, baseline - dir * peakHeight * 0.95,
                           cx,                tip);
      ctx.quadraticCurveTo(cx + halfW * 0.55, baseline - dir * peakHeight * 0.95,
                           cx + halfW,        baseline);
      ctx.stroke();
      // Inner trefoil dot
      ctx.beginPath();
      ctx.arc(cx, baseline - dir * height * 0.42, 1.2, 0, Math.PI * 2);
      ctx.fill();
    };
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
    ctx.fillStyle   = 'rgba(110, 80, 50, 0.40)';
    ctx.lineWidth   = 0.8;
    // Baselines anchor to the inner edge of each band (where body content
    // begins), arches rise into the margin space.
    const topBaseline = top.y + top.h;
    const botBaseline = bottom.y;
    const topHeight   = Math.max(8, top.h * 0.75);
    const botHeight   = Math.max(8, bottom.h * 0.75);
    for (let i = 0; i < count; i++) {
      const cx = top.x + (i + 0.5) * cellW;
      arch(cx, topBaseline, topHeight, false);
    }
    for (let i = 0; i < count; i++) {
      const cx = bottom.x + (i + 0.5) * cellW;
      arch(cx, botBaseline, botHeight, true);
    }
    // Connecting baseline that ties the arch row together
    ctx.beginPath();
    ctx.moveTo(top.x,         topBaseline); ctx.lineTo(top.x + top.w, topBaseline);
    ctx.moveTo(bottom.x,      botBaseline); ctx.lineTo(bottom.x + bottom.w, botBaseline);
    ctx.stroke();
    ctx.restore();
  },
};

// ── Registry ────────────────────────────────────────────────────────────────

const REGISTRY: Map<string, MarginGraphic> = new Map();
function register(d: MarginGraphic): void { REGISTRY.set(d.id, d); }

register(NONE);
register(GREEK_COLUMNS);
register(FLORAL_VINES);
register(ARABIC_ARCHES);

export function getMarginGraphic(id: string | undefined | null): MarginGraphic {
  if (!id) return NONE;
  return REGISTRY.get(id) ?? NONE;
}

export function listMarginGraphics(): MarginGraphic[] {
  return [...REGISTRY.values()];
}
